import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { radius } from '@/shared/theme';
import { EmptyState } from './empty-state';
import { SearchField } from './search-field';

export interface RunLocation {
  id: number;
  name: string;
}

interface LocationChecklistProps {
  locations: RunLocation[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

/** Searchable multi-select of the locations a run is queued for, with select-all. */
export function LocationChecklist({
  locations,
  selectedIds,
  onChange,
  disabled,
}: LocationChecklistProps) {
  const { t } = useTranslation('bot');
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? locations.filter((l) => l.name.toLowerCase().includes(term)) : locations;
  }, [locations, search]);

  const toggle = (id: number) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  // Select-all acts on what the search is showing, which is what the checkbox label promises.
  const visibleIds = visible.map((l) => l.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.includes(id));

  const toggleAll = () =>
    onChange(
      allVisibleSelected
        ? selectedIds.filter((id) => !visibleIds.includes(id))
        : [...new Set([...selectedIds, ...visibleIds])],
    );

  const selectedNames = locations
    .filter((l) => selectedIds.includes(l.id))
    .map((l) => l.name)
    .join(', ');

  return (
    <Box>
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder={t('run.searchLocations')}
        sx={{ mb: 1.25 }}
      />
      <Box
        sx={(theme) => ({
          maxHeight: 220,
          overflowY: 'auto',
          borderRadius: radius.control,
          border: `1px solid ${theme.vars.palette.md.border}`,
          background: theme.vars.palette.md.panel2,
          px: 1.5,
          py: 0.5,
        })}
      >
        {visible.length === 0 ? (
          <EmptyState title={t('run.noLocations')} />
        ) : (
          <>
            <FormControlLabel
              sx={(theme) => ({
                borderBottom: `1px solid ${theme.vars.palette.md.border}`,
                width: '100%',
                m: 0,
                py: 0.5,
              })}
              control={
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && someVisibleSelected}
                  onChange={toggleAll}
                  disabled={disabled}
                />
              }
              label={
                <Box component="span" sx={{ fontSize: 13, fontWeight: 700 }}>
                  {t('run.selectAll')}
                </Box>
              }
            />
            {visible.map((location) => (
              <FormControlLabel
                key={location.id}
                sx={{ width: '100%', m: 0, py: 0.25 }}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(location.id)}
                    onChange={() => toggle(location.id)}
                    disabled={disabled}
                  />
                }
                label={
                  <Box component="span" sx={{ fontSize: 13 }}>
                    {location.name}
                  </Box>
                }
              />
            ))}
          </>
        )}
      </Box>
      <Box
        sx={(theme) => ({
          mt: 1,
          fontSize: 11.5,
          fontWeight: 600,
          color: theme.vars.palette.md.faint,
        })}
      >
        {selectedIds.length === 0
          ? t('run.noneSelected')
          : `${t('run.selectedCount', { count: selectedIds.length })} · ${selectedNames}`}
      </Box>
    </Box>
  );
}
